// ============================================================================
// see-through-chooser.js — the "This or That" for a logo's enclosed areas (WD 482)
// ============================================================================
// Ryan 2026-10-31: when the app cannot know whether part of a logo should let the weight show
// through (Tigers' white fur, Carroll's black mouth), show the logo BOTH ways and let the buyer
// pick - "as a preview on a weight", not a Photoshop checkerboard: a coach knows what a plate looks
// like, not what grey squares mean.
//
// ⛔ THE SAME INTERFACE AS `yesNoToggle` ({el, get, set, setBusy}), ON PURPOSE. It drops into the
// slot the Yes/No held on both surfaces (Add Logo, Customize -> Logo), so the flip, its races, the
// resume and the save (`logo.keyGlobal`) are untouched - this is a different VIEW of one answer.
// `true` = see-through (holes cut), exactly the Yes/No's meaning.
//
// Each option is a disc in a plate colour with that version of the logo on it. The colour is chosen
// by the caller (`setPreviews`) as the plate where the difference SHOWS - on a white plate, cut-out
// white fur and kept white fur are the same picture.
// ============================================================================
import { h, replaceChildren } from './dom.js';

/* `labelClass` / `blockClass`: the Add Logo tab heads it like YOUR LOGO and YOUR COLORS (`wd-reveal-label`) and spaces
   it like their blocks (Ryan 10-31: the old sentence-case caption was "weak"). */
/* WD 498: `names` / `says` let the same two-card picker ask the BACKGROUND question too (Keep / Remove) - same cards, same
   answer shape (`true` = the second card). Defaults are the cutouts wording, so every existing caller is unchanged. */
export function seeThroughChooser({ label, caption = true, ariaLabel, value = false, onChange, labelClass = 'wd-yn-label', blockClass = '',
  names = ['Solid', 'See-through'], says = ['the enclosed areas stay filled', 'the weight shows through the enclosed areas'] }) {
  let val = !!value;
  let busy = false;
  let pv = null; // { solid, see, plate }
  const wrap = h('div', { class: 'wd-stc-row', role: 'radiogroup', 'aria-label': ariaLabel || label });
  const el = h('div', { class: 'wd-yn wd-stc' + (caption ? '' : ' wd-yn--bare') + (blockClass ? ' ' + blockClass : ''), hidden: true },
    ...(caption ? [h('span', { class: labelClass }, label)] : []), wrap);

  const option = (text, v) => {
    const url = pv ? (v ? pv.see : pv.solid) : null;
    return h('button', {
      type: 'button',
      role: 'radio',
      class: 'wd-stc-opt' + (v === val ? ' is-active' : ''),
      'aria-checked': v === val ? 'true' : 'false',
      'aria-label': `${text}: ${says[v ? 1 : 0]}`,
      onClick: () => {
        if (busy || v === val) return;
        val = v;
        render();
        onChange?.(v);
      },
    },
    h('span', { class: 'wd-stc-disc', style: pv ? `background:${pv.plate}` : '' },
      url ? h('img', { src: url, alt: '', draggable: 'false' }) : null),
    h('span', { class: 'wd-stc-name' }, text));
  };
  const render = () => replaceChildren(wrap, [option(names[0], false), option(names[1], true)]);
  render();

  return {
    el,
    get: () => val,
    /** Set the answer WITHOUT firing `onChange` (the resume / mirror path), as `yesNoToggle`. */
    set: (v) => { const n = !!v; if (n === val) return; val = n; render(); },
    setBusy: (b) => { busy = !!b; el.classList.toggle('is-busy', !!b); },
    /** Both pictures and the plate they sit on; null clears them. */
    setPreviews: (p) => { pv = p || null; render(); },
  };
}
